"use client";

import type { Option } from "@/components/tool-kit/primitives";
import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import type { TWO_PROPORTION_Z_TEST_CALCULATOR_STRINGS } from "@/components/tools/two-proportion-z-test-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type Alternative,
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type {
  OneProportionResult,
  PValues,
  TwoProportionResult,
} from "@/lib/tool-kit/stats-tests/proportions";

/** Results for the proportion z-test calculator: one proportion, or two. */

type Strings = typeof TWO_PROPORTION_Z_TEST_CALCULATOR_STRINGS;

/** The usual floor for the normal approximation to a binomial count. */
const MIN_EXPECTED_ONE = 10;
const MIN_EXPECTED_TWO = 5;

function pFor(r: PValues, alternative: Alternative): number {
  if (alternative === "less") {
    return r.pLess;
  }
  if (alternative === "greater") {
    return r.pGreater;
  }
  return r.pTwoSided;
}

interface Shared {
  alternative: Alternative;
  alpha: number;
  labels: readonly Option<Alternative>[];
  strings: Strings;
  fmt: NumberFormatter;
}

function Verdict({
  r,
  alternative,
  alpha,
  labels,
  strings,
  fmt,
}: Shared & { r: PValues }) {
  const p = pFor(r, alternative);
  return (
    <>
      <Decision reject={p < alpha}>
        {t(p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(p, fmt),
        })}
      </Decision>
      <ResultTable
        caption={strings.pTableCaption}
        head={[
          strings.alternativeColumn,
          strings.exactColumn,
          strings.apaColumn,
        ]}
        rows={labels.map((option) => ({
          key: option.value,
          cells: [
            option.label,
            formatP(pFor(r, option.value), fmt),
            `p ${apaP(pFor(r, option.value), fmt)}`,
          ],
        }))}
      />
    </>
  );
}

function interval([low, high]: [number, number], fmt: NumberFormatter): string {
  return `[${formatStat(low, fmt)}, ${formatStat(high, fmt)}]`;
}

export function OneProportionResults(
  props: Shared & { r: OneProportionResult }
) {
  const { r, alternative, strings, fmt } = props;
  const n = (v: number) => formatStat(v, fmt);
  const level = fmt(r.level, { style: "percent", maximumFractionDigits: 1 });
  const label = props.labels.find((o) => o.value === alternative)?.label ?? "";
  const small =
    r.expectedSuccesses < MIN_EXPECTED_ONE ||
    r.expectedFailures < MIN_EXPECTED_ONE;
  const apa = richTemplate(strings.apaOne, {
    z: fixed(r.z, 2, fmt),
    p: apaP(pFor(r, alternative), fmt),
    h: fixed(r.h, 2, fmt),
    phat: fixed(r.pHat, 2, fmt, false),
    level,
    low: fixed(r.wilson[0], 2, fmt, false),
    high: fixed(r.wilson[1], 2, fmt, false),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.zStatistic} value={n(r.z)} />
        <StatCard
          emphasis
          label={t(strings.pValueFor, { alternative: label })}
          value={formatP(pFor(r, alternative), fmt)}
        />
        <StatCard label={strings.pHat} value={n(r.pHat)} />
        <StatCard label={strings.se0} value={n(r.se0)} />
        <StatCard
          label={t(strings.wilson, { level })}
          value={interval(r.wilson, fmt)}
        />
        <StatCard
          label={t(strings.wald, { level })}
          value={interval(r.wald, fmt)}
        />
        <StatCard label={strings.cohensH} value={n(r.h)} />
      </StatGrid>
      <Verdict {...props} />
      {small ? (
        <p className={HINT}>
          {t(strings.smallOne, {
            successes: n(r.expectedSuccesses),
            failures: n(r.expectedFailures),
          })}
        </p>
      ) : null}
      <p className={HINT}>{strings.intervalNote}</p>
      <Working heading={strings.howCalculated}>
        <p>
          p̂ = x / n = {fmt(r.x)} / {fmt(r.n)} = {n(r.pHat)}
        </p>
        <p>
          SE₀ = √(p₀(1 − p₀) / n) = √({n(r.p0)} × {n(1 - r.p0)} / {fmt(r.n)}) ={" "}
          {n(r.se0)}
        </p>
        <p>
          z = (p̂ − p₀) / SE₀ = ({n(r.pHat)} − {n(r.p0)}) / {n(r.se0)} = {n(r.z)}
        </p>
        <p>
          {t(strings.workingWilson, {
            z: n(r.zCritical),
            ci: interval(r.wilson, fmt),
          })}
        </p>
        <p>h = 2·asin√p̂ − 2·asin√p₀ = {n(r.h)}</p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNoteOne}
        parts={apa}
      />
    </section>
  );
}

export function TwoProportionResults(
  props: Shared & { r: TwoProportionResult }
) {
  const { r, alternative, strings, fmt } = props;
  const n = (v: number) => formatStat(v, fmt);
  const level = fmt(r.level, { style: "percent", maximumFractionDigits: 1 });
  const label = props.labels.find((o) => o.value === alternative)?.label ?? "";
  const apa = richTemplate(strings.apaTwo, {
    z: fixed(r.z, 2, fmt),
    p: apaP(pFor(r, alternative), fmt),
    h: fixed(r.h, 2, fmt),
    diff: fixed(r.difference, 2, fmt, false),
    level,
    low: fixed(r.newcombe[0], 2, fmt, false),
    high: fixed(r.newcombe[1], 2, fmt, false),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.zStatistic} value={n(r.z)} />
        <StatCard
          emphasis
          label={t(strings.pValueFor, { alternative: label })}
          value={formatP(pFor(r, alternative), fmt)}
        />
        <StatCard label={strings.pHat1} value={n(r.p1)} />
        <StatCard label={strings.pHat2} value={n(r.p2)} />
        <StatCard label={strings.difference} value={n(r.difference)} />
        <StatCard label={strings.pooled} value={n(r.pooled)} />
        <StatCard
          label={t(strings.newcombe, { level })}
          value={interval(r.newcombe, fmt)}
        />
        <StatCard
          label={t(strings.waldDifference, { level })}
          value={interval(r.wald, fmt)}
        />
        <StatCard label={strings.cohensH} value={n(r.h)} />
      </StatGrid>
      <Verdict {...props} />
      {r.minExpected < MIN_EXPECTED_TWO ? (
        <p className={HINT}>{t(strings.smallTwo, { min: n(r.minExpected) })}</p>
      ) : null}
      <p className={HINT}>{strings.intervalNote}</p>
      <Working heading={strings.howCalculated}>
        <p>
          p̂₁ = {fmt(r.x1)} / {fmt(r.n1)} = {n(r.p1)}; p̂₂ = {fmt(r.x2)} /{" "}
          {fmt(r.n2)} = {n(r.p2)}; p̄ = (x₁ + x₂) / (n₁ + n₂) = {n(r.pooled)}
        </p>
        <p>
          SE = √(p̄(1 − p̄)(1/n₁ + 1/n₂)) = {n(r.sePooled)}; z = (p̂₁ − p̂₂) / SE ={" "}
          {n(r.difference)} / {n(r.sePooled)} = {n(r.z)}
        </p>
        <p>
          Wald: (p̂₁ − p̂₂) ± z × √(p̂₁(1 − p̂₁)/n₁ + p̂₂(1 − p̂₂)/n₂) ={" "}
          {n(r.difference)} ± {n(r.zCritical)} × {n(r.seUnpooled)} →{" "}
          {interval(r.wald, fmt)}
        </p>
        <p>{t(strings.workingNewcombe, { ci: interval(r.newcombe, fmt) })}</p>
        <p>h = 2·asin√p̂₁ − 2·asin√p̂₂ = {n(r.h)}</p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNoteTwo}
        parts={apa}
      />
    </section>
  );
}
